import { HttpContextToken, } from '@angular/common/http';
import { InjectionToken } from '@angular/core';

/**
 * Marks a request whose errors must reach the caller exactly as the server sent them.
 *
 * Two layers reshape HTTP errors on their way out, and both destroy the fields the auth layer needs
 * to tell a rejected credential from a temporary outage:
 *
 *  - `ErrorInterceptor` replaces `HttpErrorResponse` with `ApiError`, which carries `statusCode`
 *    rather than `status` and drops the response body;
 *  - `BaseApiService.handleError` flattens `HttpErrorResponse` into a plain `Error` carrying only a
 *    message.
 *
 * For ordinary endpoints that is helpful — components want a message. For `/auth/refresh` and
 * `/auth/logout` it is fatal: "401, the session is over" and "503, try again in a moment" arrive
 * indistinguishable, and the only safe reading of an unclassifiable failure is to end the session.
 * That is how a dropped connection became a logout.
 *
 * Requests carrying this token skip both reshapers.
 */
export const RAW_HTTP_ERRORS = new HttpContextToken<boolean>(() => false);

/**
 * Resolves once the auth cookies are current, refreshing them first if they are spent.
 *
 * SignalR negotiates over its own HTTP client, not Angular's, so `AuthInterceptor` never sees a hub
 * connection and cannot do for it what it does for every other request: notice the access token has
 * expired, refresh, and retry. Reopening a tab after the token lapsed therefore produced a burst of
 * 401s from every hub and a "Connection lost" dialog, which cleared itself moments later once an
 * ordinary API call triggered the refresh — alarming, and entirely avoidable.
 *
 * Hub services await this before connecting. Implemented by the auth library; optional, so
 * `api-client` stays usable without it.
 */
export const ENSURE_FRESH_CREDENTIALS = new InjectionToken<() => Promise<void>>(
  'ENSURE_FRESH_CREDENTIALS'
);
